import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaArrowLeft, FaArrowRight, FaTimes, FaUser, FaTrash, FaPlus, FaFileExcel, FaCheck } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { collection, query, getDocs, where, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTheme } from '../../contexts/ThemeContext';
import VisitModal from '../OperationManagement/VisitModal';
import AddPatientModal from '../ProjectsManagement/AddPatientModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useGoogleLogin } from '@react-oauth/google';
import {
  COMPLETION_STATE,
  getCompletionState,
  getCompletionTooltipLine,
  formatCompletionDate,
  getPendingCompletionLabels,
} from '../../utils/visitCompletion';
import {
  fetchAccessiblePatients,
  getVisitedPatientIds,
  normalizePatientId,
} from '../../utils/patientAccess';
import styles from './PersonalCalendar.module.css';
import { mergeByTime } from '../../utils/calendarSorting';
import './PersonalCalendarDarkMode.css';

const PersonalCalendar = () => {
  const { employeeId } = useUserProfile();
  const { isDarkMode } = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [visits, setVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [newJob, setNewJob] = useState({
    title: '',
    startDate: '',
    endDate: '',
    time: ''
  });
  const [calendarJobs, setCalendarJobs] = useState([]);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [visitPeriodBefore, setVisitPeriodBefore] = useState(''); // Başlangıç tarihi
  const [visitPeriodAfter, setVisitPeriodAfter] = useState(''); // Bitiş tarihi
  const [visitOption, setVisitOption] = useState('');
  const [patientId, setPatientId] = useState('');
  const [visitType, setVisitType] = useState('');
  const [visitNumber, setVisitNumber] = useState('');
  const [visitInfo, setVisitInfo] = useState('');
  const [showAddVisitModal, setShowAddVisitModal] = useState(false);
  const [userProjects, setUserProjects] = useState([]);
  const [visitRepeatPeriod, setVisitRepeatPeriod] = useState('');
  const [visitRepeatUnit, setVisitRepeatUnit] = useState('week');
  const [visitEndDate, setVisitEndDate] = useState('');
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [addingToCalendar, setAddingToCalendar] = useState(false);
  const [willRepeat, setWillRepeat] = useState(false);
  const [projectPatients, setProjectPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [isDeletingJob, setIsDeletingJob] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [screenFail, setScreenFail] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!employeeId) return;

        // Fetch visits
        const visitsQuery = query(
          collection(db, 'Visits'),
          where('employeeId', '==', employeeId)
        );
        const visitsSnapshot = await getDocs(visitsQuery);
        const visitsData = visitsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setVisits(visitsData);

        // Fetch calendar jobs
        const jobsQuery = query(
          collection(db, 'CalendarJobs'),
          where('employeeId', '==', employeeId)
        );
        const jobsSnapshot = await getDocs(jobsQuery);
        const jobsData = jobsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCalendarJobs(jobsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [employeeId]);

  useEffect(() => {
    const fetchUserProjects = async () => {
      if (!employeeId) return;
      
      try {
        const projectsRef = collection(db, 'Projects');
        const projectsSnapshot = await getDocs(projectsRef);
        const allProjects = projectsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter projects where the employee is assigned
        const userProjs = allProjects.filter(project => 
          project.employees && 
          Object.values(project.employees).some(emp => emp.id === employeeId)
        );

        setUserProjects(userProjs);
      } catch (error) {
        console.error('Error fetching user projects:', error);
      }
    };

    fetchUserProjects();
  }, [employeeId]);

  // Patient ids this user already booked a visit with, in the selected project.
  // The calendar already holds every visit of the logged in employee, so no
  // extra read is needed here.
  const visitedPatientIds = useMemo(
    () => getVisitedPatientIds(visits, selectedProject, employeeId),
    [visits, selectedProject, employeeId]
  );

  // Fetch the patients this user is allowed to pick: the ones they already have
  // a visit with, plus the ones they created themselves. Other employees'
  // patients are never requested.
  const fetchProjectPatients = useCallback(async (projectId, ownVisitedPatientIds) => {
    if (!projectId || !employeeId) {
      setProjectPatients([]);
      return;
    }

    setLoadingPatients(true);
    try {
      const patientsData = await fetchAccessiblePatients(projectId, employeeId, {
        visitedPatientIds: ownVisitedPatientIds,
      });
      setProjectPatients(patientsData);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setProjectPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  }, [employeeId]);

  // Handler for when a new patient is added
  const handlePatientAdded = (savedPatient) => {
    setShowAddPatientModal(false);

    // A patient the user just created belongs to them, so show and select it
    // right away instead of waiting for the refresh below.
    if (savedPatient?.patientId) {
      setProjectPatients(prev =>
        prev.some(patient => patient.id === savedPatient.id)
          ? prev
          : [...prev, savedPatient].sort(
              (a, b) => (a.patientId || '').localeCompare(b.patientId || '')
            )
      );
      setPatientId(savedPatient.patientId);
    }

    // Refresh the patients list for the selected project
    if (selectedProject) {
      fetchProjectPatients(selectedProject, visitedPatientIds);
    }
  };

  // Fetch patients when the selected project - or the user's own visits - change
  useEffect(() => {
    if (selectedProject) {
      fetchProjectPatients(selectedProject, visitedPatientIds);
    } else {
      setProjectPatients([]);
    }
  }, [selectedProject, visitedPatientIds, fetchProjectPatients]);

  // Reset patient selection when project changes
  useEffect(() => {
    setPatientId('');
  }, [selectedProject]);

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Helper function to normalize date to start of day in local timezone
  const normalizeDate = useCallback((date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }, []);

  // Get visits for a specific day (memoized)
  const getVisitsForDay = useCallback((date) => {
    return visits.filter(visit => {
      const firstVisitDate = normalizeDate(new Date(visit.firstVisitDate));
      const period = visit.period || '0';
      
      let startDate, endDate;
      
      // Check if period is in new format "-X,+Y" or old format (number)
      if (typeof period === 'string' && period.includes(',')) {
        // New format: "-2,+3"
        const parts = period.split(',');
        const beforeDays = Math.abs(parseInt(parts[0]) || 0);
        const afterDays = Math.abs(parseInt(parts[1]) || 0);
        
        startDate = new Date(firstVisitDate);
        startDate.setDate(firstVisitDate.getDate() - beforeDays);
        
        endDate = new Date(firstVisitDate);
        endDate.setDate(firstVisitDate.getDate() + afterDays);
      } else {
        // Old format: single number
        const periodNum = typeof period === 'number' ? period : parseInt(period) || 1;
        
        if (periodNum >= 0) {
          // Positive period: start from firstVisitDate, extend forward
          startDate = new Date(firstVisitDate);
          endDate = new Date(firstVisitDate);
          endDate.setDate(firstVisitDate.getDate() + (periodNum - 1));
        } else {
          // Negative period: start from (firstVisitDate + period), end at firstVisitDate
          startDate = new Date(firstVisitDate);
          startDate.setDate(firstVisitDate.getDate() + periodNum + 1);
          endDate = new Date(firstVisitDate);
        }
      }
      
      const currentDateToCheck = normalizeDate(new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        date
      ));

      return currentDateToCheck >= startDate && 
             currentDateToCheck <= endDate &&
             currentDateToCheck.getMonth() === currentDate.getMonth() &&
             currentDateToCheck.getFullYear() === currentDate.getFullYear();
    });
  }, [visits, currentDate, normalizeDate]);

  // Check if it's the first day of a visit (memoized)
  const isFirstDayOfVisit = useCallback((visit, date) => {
    const visitDate = normalizeDate(new Date(visit.firstVisitDate));
    const checkDate = normalizeDate(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      date
    ));
    
    return visitDate.getTime() === checkDate.getTime();
  }, [currentDate, normalizeDate]);

  // Calculate remaining days for a visit (memoized)
  const getRemainingDays = useCallback((visit, date) => {
    const visitDate = new Date(visit.firstVisitDate);
    const currentDateToCheck = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      date
    );
    
    const period = visit.period || '0';
    let endDate;
    
    // Check if period is in new format "-X,+Y" or old format (number)
    if (typeof period === 'string' && period.includes(',')) {
      // New format: "-2,+3"
      const parts = period.split(',');
      const afterDays = Math.abs(parseInt(parts[1]) || 0);
      
      endDate = new Date(visitDate);
      endDate.setDate(visitDate.getDate() + afterDays);
    } else {
      // Old format: single number
      const periodNum = typeof period === 'number' ? period : parseInt(period) || 1;
      
      if (periodNum >= 0) {
        // Positive period: end date is forward from firstVisitDate
        endDate = new Date(visitDate);
        endDate.setDate(visitDate.getDate() + (periodNum - 1));
      } else {
        // Negative period: end date is the firstVisitDate itself
        endDate = new Date(visitDate);
      }
    }
    
    const remainingDays = Math.ceil((endDate - currentDateToCheck) / (1000 * 60 * 60 * 24)) + 1;
    return remainingDays;
  }, [currentDate]);

  // Calendar helper functions
  const getMonthName = (date) => {
    const monthNames = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];
    return monthNames[date.getMonth()];
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    // getDay() returns 0 for Sunday, 1 for Monday, etc.
    // Calendar starts with Monday (Pzt), so shift: Mon=0 ... Sun=6
    return (new Date(year, month, 1).getDay() + 6) % 7;
  };

  const handleVisitClick = (visit) => {
    setSelectedVisit(visit);
    setShowVisitModal(true);
  };

  const handleNewJobSubmit = async (e) => {
    e.preventDefault();
    try {
      // Add employeeId to the job data
      const jobData = {
        ...newJob,
        employeeId,
        createdAt: new Date().toISOString(),
        status: false
      };

      // Add document to CalendarJobs collection
      const docRef = await addDoc(collection(db, 'CalendarJobs'), jobData);
      
      // Add the new job to the state with its ID
      const newJobWithId = {
        id: docRef.id,
        ...jobData
      };
      setCalendarJobs(prevJobs => [...prevJobs, newJobWithId]);
      
      // Show success toast
      toast.success('İş başarıyla eklendi!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Clear form and close modal
      setShowNewJobModal(false);
      setNewJob({ title: '', startDate: '', endDate: '', time: '' });
    } catch (error) {
      console.error('Error adding job:', error);
      toast.error('İş eklenirken bir hata oluştu!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const getJobsForDay = useCallback((date) => {
    return calendarJobs.filter(job => {
      const startDate = normalizeDate(new Date(job.startDate));
      const endDate = normalizeDate(new Date(job.endDate));
      
      const currentDateToCheck = normalizeDate(new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        date
      ));

      return currentDateToCheck >= startDate && 
             currentDateToCheck <= endDate &&
             currentDateToCheck.getMonth() === currentDate.getMonth() &&
             currentDateToCheck.getFullYear() === currentDate.getFullYear();
    });
  }, [calendarJobs, currentDate, normalizeDate]);

  const isFirstDayOfJob = useCallback((job, date) => {
    const jobDate = normalizeDate(new Date(job.startDate));
    const checkDate = normalizeDate(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      date
    ));
    
    return jobDate.getTime() === checkDate.getTime();
  }, [currentDate, normalizeDate]);

  const getJobRemainingDays = useCallback((job, date) => {
    const currentDateToCheck = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      date
    );
    const endDate = new Date(job.endDate);
    
    const remainingDays = Math.ceil((endDate - currentDateToCheck) / (1000 * 60 * 60 * 24)) + 1;
    return remainingDays;
  }, [currentDate]);

  // Helper function to calculate which day number it is (memoized)
  const getDayNumber = useCallback((visit, currentDay) => {
    const firstVisitDate = normalizeDate(new Date(visit.firstVisitDate));
    const current = normalizeDate(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDay
    ));
    
    // Calculate difference in days (can be negative)
    const diffTime = current - firstVisitDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    // If current day is before first visit date, return negative number
    // If current day is first visit date, return 0
    // If current day is after first visit date, return positive number
    return diffDays;
  }, [currentDate, normalizeDate]);

  // Helper function for job day numbers (always positive)
  const getJobDayNumber = useCallback((startDate, currentDay) => {
    const start = normalizeDate(new Date(startDate));
    const current = normalizeDate(new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDay
    ));
    
    const diffTime = Math.abs(current - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  }, [currentDate, normalizeDate]);

  // Google Calendar integration
  const loginForCalendarEvent = useGoogleLogin({
    scope: [
      'https://www.googleapis.com/auth/calendar',
      'https://www.googleapis.com/auth/calendar.events',
      'profile',
      'email'
    ].join(' '),
    onSuccess: async (tokenResponse) => {
      try {
        // Get the selected project name
        const selectedProjectObj = userProjects.find(p => p.id === selectedProject);
        const projectName = selectedProjectObj ? (selectedProjectObj.titleShort || selectedProjectObj.title) : 'Ziyaret';
        
        await addToGoogleCalendar(tokenResponse.access_token, projectName);
      } catch (err) {
        console.error('Takvim hatası:', err);
        toast.error('Google Takvim\'e eklenirken bir hata oluştu. Lütfen tekrar deneyin.');
        setAddingToCalendar(false);
      }
    },
    onError: (error) => {
      console.error('Giriş hatası:', error);
      toast.error('Google girişi başarısız oldu. Lütfen tekrar deneyin.');
      setAddingToCalendar(false);
    },
  });

  const addToGoogleCalendar = async (accessToken, projectName) => {
    setAddingToCalendar(true);
    
    try {
      // Make sure we have valid dates
      if (!visitPeriodBefore || !visitPeriodAfter) {
        throw new Error('Ziyaret penceresi tarihleri geçerli değil');
      }
      
      // Use the window dates directly
      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      endDate.setDate(endDate.getDate() + 1); // Google Calendar end date is exclusive
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Tarih hesaplanamadı');
      }
      
      // Format dates for all-day event (YYYY-MM-DD format)
      const formatDateForAllDay = (date) => {
        const year = date.getFullYear();
        // Month is 0-indexed in JS, so add 1 and pad with leading zero if needed
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      // Create event data for an all-day event
      const eventData = {
        summary: `Ziyaret: ${projectName}`,
        description: `Hasta ID: ${patientId}\n${visitInfo || ''}`,
        location: '',
        start: {
          date: formatDateForAllDay(startDate),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          date: formatDateForAllDay(endDate),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      // Make API request to Google Calendar
      const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Takvim etkinliği oluşturulamadı: ${errorData.error?.message || 'Bilinmeyen hata'}`);
      }

      toast.success('Ziyaret başarıyla Google Takvim\'e eklendi!');
    } catch (err) {
      console.error('Detaylı hata:', err);
      toast.error(`Takvim etkinliği oluşturulamadı: ${err.message}`);
    } finally {
      setAddingToCalendar(false);
    }
  };

  const handleVisitSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate period from dates
      if (!visitPeriodBefore || !visitPeriodAfter || !visitDate) {
        toast.error('Lütfen tüm tarih alanlarını doldurun!');
        return;
      }

      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      const firstVisit = new Date(visitDate);

      // Calculate days before and after
      const daysBefore = Math.ceil((firstVisit - startDate) / (1000 * 60 * 60 * 24));
      const daysAfter = Math.ceil((endDate - firstVisit) / (1000 * 60 * 60 * 24));

      // Validate dates
      if (startDate > firstVisit || endDate < firstVisit) {
        toast.error('İlk ziyaret tarihi, başlangıç ve bitiş tarihleri arasında olmalıdır!');
        return;
      }

      const periodString = daysBefore > 0 || daysAfter > 0 ? `-${daysBefore},+${daysAfter}` : '0';
      const totalDays = daysBefore + daysAfter;
      
      const baseVisitData = {
        projectId: selectedProject,
        employeeId: employeeId,
        period: periodString, // Store as "-2,+3" format
        option: Number(visitOption),
        patientId: normalizePatientId(patientId),
        visitType: visitType || '',
        visitNumber: visitNumber ? Number(visitNumber) : '',
        info: visitInfo,
        createdAt: new Date(),
        preVisitForm: false,
        screenFail: visitType === 'hastane' && screenFail ? true : false
      };

      const startDateTime = new Date(visitDate);
      const visitPromises = [];

      if (willRepeat) {
        // If repeating, create visits until end date
        const endDateTime = new Date(visitEndDate);
        const repeatPeriod = Number(visitRepeatPeriod);
        
        let currentVisitDate = new Date(startDateTime);
        while (currentVisitDate <= endDateTime) {
          const visitData = {
            ...baseVisitData,
            firstVisitDate: currentVisitDate.toISOString().split('T')[0],
          };

          visitPromises.push(addDoc(collection(db, 'Visits'), visitData));

          // Calculate next visit date based on selected unit
          currentVisitDate = new Date(currentVisitDate);
          if (visitRepeatUnit === 'week') {
            currentVisitDate.setDate(currentVisitDate.getDate() + (7 * repeatPeriod));
          } else {
            // For 'day' unit, add the period days plus the total duration
            currentVisitDate.setDate(currentVisitDate.getDate() + repeatPeriod + totalDays);
          }
        }
      } else {
        // If not repeating, just create a single visit
        const visitData = {
          ...baseVisitData,
          firstVisitDate: startDateTime.toISOString().split('T')[0],
        };
        visitPromises.push(addDoc(collection(db, 'Visits'), visitData));
      }

      await Promise.all(visitPromises);
      
      console.log('Visit added with period:', periodString, 'First visit date:', visitDate);
      
      // If user checked "Add to Calendar", open Google Calendar link
      if (addToCalendar) {
        // Get the selected project name
        const selectedProjectObj = userProjects.find(p => p.id === selectedProject);
        const projectName = selectedProjectObj ? (selectedProjectObj.titleShort || selectedProjectObj.title) : 'Ziyaret';
        
        // Create Google Calendar URL
        const calendarUrl = createGoogleCalendarUrl(projectName);
        window.open(calendarUrl, '_blank');
      }
      
      // Reset form
      setSelectedProject('');
      setVisitDate('');
      setVisitEndDate(''); 
      setVisitPeriodBefore('');
      setVisitPeriodAfter('');
      setVisitOption('');
      setPatientId('');
      setVisitInfo('');
      setVisitRepeatPeriod('');
      setAddToCalendar(false);
      setWillRepeat(false);
      setScreenFail(false);
      
      // Close modal
      setShowAddVisitModal(false);
      
      // Show success message
      toast.success('Visit(ler) başarıyla eklendi!');
      
      // Refresh visits
      const visitsQuery = query(
        collection(db, 'Visits'),
        where('employeeId', '==', employeeId)
      );
      const visitsSnapshot = await getDocs(visitsQuery);
      const visitsData = visitsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVisits(visitsData);
      console.log('Visits refreshed, total:', visitsData.length, 'Latest visit:', visitsData[visitsData.length - 1]);
    } catch (error) {
      console.error('Error adding visits:', error);
      toast.error('Visit eklenirken bir hata oluştu!');
    }
  };

  // Function to toggle job completion status
  const handleToggleJobStatus = async () => {
    if (!selectedJob?.id) return;

    try {
      const jobRef = doc(db, 'CalendarJobs', selectedJob.id);
      const newStatus = !selectedJob.status;
      await updateDoc(jobRef, {
        status: newStatus,
        lastUpdated: new Date().toISOString()
      });

      // Update local state
      const updatedJob = { ...selectedJob, status: newStatus };
      setCalendarJobs(prevJobs =>
        prevJobs.map(job => job.id === selectedJob.id ? { ...job, status: newStatus } : job)
      );
      setSelectedJob(updatedJob);

      toast.success(newStatus ? 'İş tamamlandı olarak işaretlendi!' : 'İş devam ediyor olarak işaretlendi!');
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error('İş durumu güncellenirken bir hata oluştu!');
    }
  };

  // Function to delete a calendar job
  const handleDeleteJob = async () => {
    if (!selectedJob?.id) return;

    const confirmDelete = window.confirm(
      'Bu işi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'
    );

    if (!confirmDelete) return;

    setIsDeletingJob(true);
    try {
      const jobRef = doc(db, 'CalendarJobs', selectedJob.id);
      await deleteDoc(jobRef);

      // Remove from state
      setCalendarJobs(prevJobs => 
        prevJobs.filter(job => job.id !== selectedJob.id)
      );

      toast.success('İş başarıyla silindi!');
      setShowJobModal(false);
      setSelectedJob(null);
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('İş silinirken bir hata oluştu!');
    } finally {
      setIsDeletingJob(false);
    }
  };

  // Export visits for the currently displayed month to Excel
  const handleExcelExport = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth(); // 0-indexed

    // Build a project lookup map
    const projectMap = {};
    userProjects.forEach(p => {
      projectMap[p.id] = p.titleShort || p.title || p.id;
    });

    const monthStart = new Date(year, month, 1);
    monthStart.setHours(0, 0, 0, 0);
    const monthEnd = new Date(year, month + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Collect all visits that fall within this month
    const rows = [];
    visits.forEach(visit => {
      const firstVisitDate = normalizeDate(new Date(visit.firstVisitDate));
      const period = visit.period || '0';

      let startDate, endDate;
      if (typeof period === 'string' && period.includes(',')) {
        const parts = period.split(',');
        const beforeDays = Math.abs(parseInt(parts[0]) || 0);
        const afterDays = Math.abs(parseInt(parts[1]) || 0);
        startDate = new Date(firstVisitDate);
        startDate.setDate(firstVisitDate.getDate() - beforeDays);
        endDate = new Date(firstVisitDate);
        endDate.setDate(firstVisitDate.getDate() + afterDays);
      } else {
        const periodNum = typeof period === 'number' ? period : parseInt(period) || 1;
        if (periodNum >= 0) {
          startDate = new Date(firstVisitDate);
          endDate = new Date(firstVisitDate);
          endDate.setDate(firstVisitDate.getDate() + (periodNum - 1));
        } else {
          startDate = new Date(firstVisitDate);
          startDate.setDate(firstVisitDate.getDate() + periodNum + 1);
          endDate = new Date(firstVisitDate);
        }
      }

      // Only include if the visit window overlaps with the current month
      if (startDate > monthEnd || endDate < monthStart) return;

      // Determine status label
      const visitEndDeadline = new Date(endDate);
      visitEndDeadline.setHours(23, 59, 59, 999);
      visitEndDeadline.setTime(visitEndDeadline.getTime() + 72 * 60 * 60 * 1000);

      let durum;
      if (visit.screenFail) {
        durum = 'Screen Fail';
      } else if (visit.status) {
        durum = 'Tamamlandı';
      } else if (visitEndDeadline < new Date()) {
        durum = 'Tamamlanmadı';
      } else if (firstVisitDate > today) {
        durum = 'Planlanan';
      } else {
        durum = 'Devam Ediyor';
      }

      const formatDate = (d) => {
        if (!d) return '';
        const nd = new Date(d);
        return `${nd.getDate().toString().padStart(2, '0')}.${(nd.getMonth() + 1).toString().padStart(2, '0')}.${nd.getFullYear()}`;
      };

      rows.push({
        'Hasta ID': visit.patientId || '-',
        'Proje': projectMap[visit.projectId] || visit.projectId || '-',
        'İlk Ziyaret Tarihi': formatDate(visit.firstVisitDate),
        'Pencere Başlangıcı': formatDate(startDate),
        'Pencere Bitişi': formatDate(endDate),
        'Ziyaret Tipi': visit.visitType || '-',
        'Ziyaret Numarası': visit.visitNumber !== undefined && visit.visitNumber !== '' ? visit.visitNumber : '-',
        'Durum': durum,
        'Tamamlanma Tarihi': formatCompletionDate(visit, { withTime: true }) || '-',
        'Screen Fail': visit.screenFail ? 'Evet' : 'Hayır',
        'Notlar': visit.info || '-',
        'Pre-Visit Form': visit.preVisitForm ? 'Evet' : 'Hayır',
      });
    });

    if (rows.length === 0) {
      toast.info('Bu ay için gösterilecek visit bulunamadı.');
      return;
    }

    const monthNames = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Auto column widths
    const colWidths = Object.keys(rows[0]).map(key => ({
      wch: Math.max(key.length, ...rows.map(r => String(r[key] || '').length)) + 2
    }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Visitler');
    XLSX.writeFile(workbook, `Visitler_${monthNames[month]}_${year}.xlsx`);
  }, [currentDate, visits, userProjects, normalizeDate]);

  // Function to create Google Calendar URL
  const createGoogleCalendarUrl = (projectName) => {
    try {
      // Use the window dates directly
      const startDate = new Date(visitPeriodBefore);
      const endDate = new Date(visitPeriodAfter);
      endDate.setDate(endDate.getDate() + 1); // Google Calendar end date is exclusive
      
      // Format dates for Google Calendar URL
      const formatDateForGCal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
      };
      
      const startDateFormatted = formatDateForGCal(startDate);
      const endDateFormatted = formatDateForGCal(endDate);
      
      // Create event details
      const eventTitle = encodeURIComponent(`Ziyaret: ${projectName}`);
      const eventLocation = encodeURIComponent('');
      const eventDetails = encodeURIComponent(
        `Hasta ID: ${patientId || 'Belirtilmemiş'}\n` +
        `${visitInfo || ''}`
      );
      
      // Create Google Calendar URL for all-day event
      return `https://www.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&dates=${startDateFormatted}/${endDateFormatted}&details=${eventDetails}&location=${eventLocation}&sf=true&output=xml`;
    } catch (error) {
      console.error('Error creating calendar URL:', error);
      return '';
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.calendarCellEmpty}></div>);
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayVisits = getVisitsForDay(day);
      const dayJobs = getJobsForDay(day);
      
      const currentDateToCheck = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      currentDateToCheck.setHours(0, 0, 0, 0);
      
      const isToday = currentDateToCheck.getTime() === today.getTime();
      const isPast = currentDateToCheck < today;
      
      // Vizit ve iş kartlarının çizimi; saat sıralı tek listede
      // hangi tür olduğuna göre çağrılır.
      const renderVisitChip = (visit) => {

              const visitDate = new Date(visit.firstVisitDate);
              visitDate.setHours(0, 0, 0, 0);

              const period = visit.period || 1;
              const visitEndDate = new Date(visitDate);
              if (period >= 0) {
                visitEndDate.setDate(visitDate.getDate() + (period - 1));
              }
              // Add 72-hour grace period after visit end date
              const deadline = new Date(visitEndDate);
              deadline.setHours(23, 59, 59, 999);
              deadline.setTime(deadline.getTime() + (72 * 60 * 60 * 1000));
              
              // A completed visit only reads as "done" on the day it actually
              // happened; the rest of its window is shown as closed so the
              // completion day stands out at a glance.
              const completionState = getCompletionState(visit, currentDateToCheck);

              let visitClass = '';
              if (completionState === COMPLETION_STATE.COMPLETED_DAY) {
                visitClass = `${styles.completed} ${styles.completedOnDay}`;
              } else if (completionState === COMPLETION_STATE.WINDOW_CLOSED) {
                visitClass = styles.windowClosed;
              } else if (completionState === COMPLETION_STATE.COMPLETED_UNKNOWN) {
                // Legacy visit with no recorded completion date — keep the old look.
                visitClass = styles.completed;
              } else if (deadline < new Date()) {
                visitClass = styles.past;
              } else if (visitDate.getTime() === today.getTime()) {
                visitClass = styles.today;
              } else if (visitDate > today) {
                visitClass = styles.future;
              } else {
                visitClass = styles.today;
              }

              // Two visits of the same patient can overlap inside their windows
              // on the same day. The visit number is what tells them apart, so
              // it leads the chip and survives the truncation.
              const dayNum = getDayNumber(visit, day);
              const dayLabel = dayNum === 0
                ? 'İlk Ziyaret'
                : dayNum > 0 ? `+${dayNum}. Gün` : `${dayNum}. Gün`;
              const patientLabel = normalizePatientId(visit.patientId) || 'Hasta ID yok';
              const visitNumberLabel = visit.visitNumber ? `V${visit.visitNumber}` : '';
              const pendingCompletionLabels = getPendingCompletionLabels(visit);

              return (
                <div
                  key={visit.id}
                  onClick={() => handleVisitClick(visit)}
                  className={`${styles.visitItem} ${
                    isFirstDayOfVisit(visit, day) ? styles.visitFirstDay : ''
                  } ${visitClass} ${visit.screenFail ? styles.screenFailVisit : ''}`}
                  onMouseEnter={(e) => {
                    const statusLine = visit.screenFail
                      ? 'Screen Fail'
                      : getCompletionTooltipLine(visit, currentDateToCheck)
                        || (visitDate < today ? 'Tamamlanmadı' : 'Devam Ediyor');
                    const content = [
                      `Patient ID: ${patientLabel}`,
                      visit.visitNumber ? `Visit: ${visit.visitNumber}. Visit` : null,
                      `Day: ${dayLabel}`,
                      `Durum: ${statusLine}`,
                    ].filter(Boolean).join('\n');
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipContent(content);
                    setTooltipPosition({ x: rect.left, y: rect.bottom });
                    setShowTooltip(true);
                  }}
                  onMouseLeave={() => {
                    setShowTooltip(false);
                  }}
                >
                  {visitNumberLabel && (
                    <span className={styles.visitNumberBadge}>{visitNumberLabel}</span>
                  )}
                  <span className={styles.visitItemLabel}>
                    {patientLabel} ({dayLabel})
                  </span>
                  {completionState === COMPLETION_STATE.COMPLETED_DAY && (
                    <FaCheck className={styles.completedCheck} aria-hidden="true" />
                  )}
                  {/* Completed, but D — Tamamlanma Tarihleri are outstanding */}
                  {pendingCompletionLabels.length > 0 && (
                    <span
                      className={styles.signaturePendingBadge}
                      title={`Eksik tamamlanma tarihi: ${pendingCompletionLabels.join(', ')}`}
                    >
                      Eksik
                    </span>
                  )}
                </div>
              );
      };

      const renderJobChip = (job) => {

              const jobDate = new Date(job.startDate);
              jobDate.setHours(0, 0, 0, 0);
              
              let jobClass = '';
              if (job.status) {
                jobClass = styles.jobCompleted;
              } else if (jobDate.getTime() === today.getTime()) {
                jobClass = styles.today;
              } else if (jobDate > today) {
                jobClass = styles.future;
              } else {
                jobClass = styles.past;
              }

              return (
                <div 
                  key={job.id}
                  className={`${styles.visitItem} ${
                    isFirstDayOfJob(job, day) ? styles.visitFirstDay : ''
                  } ${styles.calendarJob} ${jobClass}`}
                  onClick={() => {
                    setSelectedJob(job);
                    setShowJobModal(true);
                  }}
                  onMouseEnter={(e) => {
                    const content = `
                      ${job.title}
                      Gün: ${getJobDayNumber(job.startDate, day)}
                      ${job.time ? `Saat: ${job.time}` : ''}
                      Durum: ${job.status ? 'Tamamlandı' : 'Devam Ediyor'}
                    `;
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipContent(content);
                    setTooltipPosition({ x: rect.left, y: rect.bottom });
                    setShowTooltip(true);
                  }}
                  onMouseLeave={() => {
                    setShowTooltip(false);
                  }}
                >
                  {job.title} ({getJobDayNumber(job.startDate, day)}. Gün)
                </div>
              );
      };

      days.push(
        <div
          key={day}
          className={`${styles.calendarCell} ${isToday ? styles.calendarCellHighlighted : ''} ${isPast ? styles.past : ''}`}
        >
          <div className={styles.dayNumber}>{day}</div>
          <div className={styles.visitsList}>
            {/* Vizitler ve işler tek listede, saat sırasına göre gösterilir */}
            {mergeByTime(dayVisits, dayJobs).map((entry) =>
              entry.kind === 'visit'
                ? renderVisitChip(entry.item)
                : renderJobChip(entry.item)
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  return (
    <div className={`${styles.calendarWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={styles.legendContainer}>
        <div className={styles.legendItems}>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.completedDot}`}>
              <FaCheck aria-hidden="true" />
            </div>
            <span className={styles.legendText}>: Tamamlandığı gün</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.windowClosedDot}`}></div>
            <span className={styles.legendText}>: Pencere kapandı</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.inProgressDot}`}></div>
            <span className={styles.legendText}>: Devam Ediyor</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.overdueDot}`}></div>
            <span className={styles.legendText}>: Tamamlanmadı</span>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendDot} ${styles.plannedDot}`}></div>
            <span className={styles.legendText}>: Planlanan</span>
          </div>
        </div>
        <div className={styles.buttonGroup}>
          <button
            onClick={handleExcelExport}
            className={`${styles.addButton} ${styles.excelButton}`}
          >
            <FaFileExcel style={{ marginRight: '6px' }} />
            Excele İndir
          </button>
          <button 
            onClick={() => setShowAddVisitModal(true)}
            className={styles.addButton}
          >
            Yeni Visit Ekle
          </button>
          <button 
            onClick={() => setShowNewJobModal(true)}
            className={styles.addButton}
          >
            Yeni İş Ekle
          </button>
        </div>
      </div>
      <div className={styles.calendarHeader}>
        <button 
          onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} 
          className={styles.iconButton}
        >
          <FaArrowLeft />
        </button>
        <span className={styles.calendarTitle}>
          {getMonthName(currentDate)} {currentDate.getFullYear()}
        </span>
        <button 
          onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} 
          className={styles.iconButton}
        >
          <FaArrowRight />
        </button>
      </div>
      <div className={styles.daysOfWeek}>
        {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map((day) => (
          <div key={day} className={styles.dayLabel}>
            {day}
          </div>
        ))}
      </div>
      <div className={styles.calendarGrid}>
        {renderCalendarDays()}
      </div>

      <VisitModal 
        show={showVisitModal}
        onHide={() => setShowVisitModal(false)}
        visit={selectedVisit}
        onVisitUpdate={(updatedVisit) => {
          if (updatedVisit.deleted) {
            // Remove deleted visit from state
            setVisits(prevVisits => 
              prevVisits.filter(visit => visit.id !== updatedVisit.id)
            );
          } else {
            setVisits(prevVisits => 
              prevVisits.map(visit => 
                visit.id === updatedVisit.id ? updatedVisit : visit
              )
            );
          }
        }}
      />

      {showNewJobModal && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Yeni İş Ekle</h3>
              <button 
                onClick={() => setShowNewJobModal(false)}
                className={styles.closeButton}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleNewJobSubmit} className={styles.modalForm}>
              <div className={styles.inputGroup}>
                <label className={styles.label}>İş Başlığı</label>
                <input
                  type="text"
                  value={newJob.title}
                  onChange={(e) => setNewJob({...newJob, title: e.target.value})}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={newJob.startDate}
                  onChange={(e) => setNewJob({...newJob, startDate: e.target.value})}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Bitiş Tarihi</label>
                <input
                  type="date"
                  value={newJob.endDate}
                  onChange={(e) => setNewJob({...newJob, endDate: e.target.value})}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Saat</label>
                <input
                  type="time"
                  value={newJob.time}
                  onChange={(e) => setNewJob({...newJob, time: e.target.value})}
                  className={styles.input}
                />
              </div>
              <button type="submit" className={styles.submitButton}>
                Kaydet
              </button>
            </form>
          </div>
        </div>
      )}

      {showAddVisitModal && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Yeni Visit Ekle</h3>
              <button 
                onClick={() => setShowAddVisitModal(false)}
                className={styles.closeButton}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleVisitSubmit} className={styles.modalForm}>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Proje</label>
                <select
                  className={styles.input}
                  required
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <option value="">Proje Seçiniz</option>
                  {userProjects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.titleShort || project.title}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedProject && (
                <div className={styles.inputGroup}>
                  <label className={styles.label}>
                    <FaUser style={{ marginRight: '6px' }} />
                    Hasta ID
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {loadingPatients ? (
                      <div className={styles.input} style={{ display: 'flex', alignItems: 'center', color: '#718096', flex: 1 }}>
                        Hastalar yükleniyor...
                      </div>
                    ) : projectPatients.length > 0 ? (
                      <select
                        className={styles.input}
                        style={{ flex: 1 }}
                        value={patientId}
                        onChange={(e) => setPatientId(e.target.value)}
                      >
                        <option value="">Hasta Seçiniz (Opsiyonel)</option>
                        {projectPatients.map(patient => (
                          <option key={patient.id} value={patient.patientId}>
                            {patient.patientId} {patient.status ? `(${patient.status})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className={styles.input} style={{ display: 'flex', alignItems: 'center', color: '#718096', fontSize: '0.9rem', flex: 1 }}>
                        Bu projede size ait hasta bulunamadı - yandaki + ile ekleyebilirsiniz
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowAddPatientModal(true)}
                      className={styles.addPatientButton}
                      title="Yeni Hasta Ekle"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              )}
              
              <div className={styles.inputGroup}>
                <label className={styles.label}>İlk Ziyaret Tarihi</label>
                <input
                  type="date"
                  className={styles.input}
                  required
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                />
              </div>
              
              <div className={styles.inputGroup}>
                <label className={styles.label}>Ziyaret Penceresi</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
                      Başlangıç Tarihi
                    </label>
                    <input
                      type="date"
                      className={styles.input}
                      required
                      value={visitPeriodBefore}
                      onChange={(e) => setVisitPeriodBefore(e.target.value)}
                      max={visitDate || undefined}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
                      Bitiş Tarihi
                    </label>
                    <input
                      type="date"
                      className={styles.input}
                      required
                      value={visitPeriodAfter}
                      onChange={(e) => setVisitPeriodAfter(e.target.value)}
                      min={visitDate || undefined}
                    />
                  </div>
                </div>
                {visitDate && visitPeriodBefore && visitPeriodAfter && (
                  <div style={{ 
                    marginTop: '0.5rem', 
                    padding: '0.5rem', 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: '4px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <small style={{ fontSize: '0.75rem', color: '#1e40af', display: 'block', fontWeight: '500' }}>
                      📅 Ziyaret: {new Date(visitDate).toLocaleDateString('tr-TR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </small>
                    <small style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                      Pencere: {Math.ceil((new Date(visitDate) - new Date(visitPeriodBefore)) / (1000 * 60 * 60 * 24))} gün önce başlar, 
                      {' '}{Math.ceil((new Date(visitPeriodAfter) - new Date(visitDate)) / (1000 * 60 * 60 * 24))} gün sonra biter
                      {' '}(Toplam {Math.ceil((new Date(visitPeriodAfter) - new Date(visitPeriodBefore)) / (1000 * 60 * 60 * 24))} gün)
                    </small>
                  </div>
                )}
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Ziyaret Tipi</label>
                <select
                  className={styles.input}
                  value={visitType}
                  onChange={(e) => setVisitType(e.target.value)}
                >
                  <option value="">Lütfen seçiniz</option>
                  <option value="hastane">Hastane Vizit</option>
                  <option value="home">Home Vizit</option>
                  <option value="telefon">Telefon Vizit</option>
                  <option value="unplanning">Unplanning Vizit</option>
                  <option value="handover">Handover Vizit</option>
                </select>
              </div>
              {visitType === 'hastane' && (
                <div className={styles.checkboxGroup}>
                  <label className={styles.label}>
                    <input
                      type="checkbox"
                      checked={screenFail}
                      onChange={(e) => setScreenFail(e.target.checked)}
                    />
                    Screen Fail mi?
                  </label>
                  {screenFail && (
                    <small className={styles.screenFailNote}>
                      Screen fail olarak işaretlenen kayıtlar ziyaret olarak sayılmaz, yapılan iş olarak kaydedilir.
                    </small>
                  )}
                </div>
              )}
              <div className={styles.inputGroup}>
                <label className={styles.label}>Kaçıncı Visit</label>
                <input
                  type="number"
                  min="1"
                  className={styles.input}
                  value={visitNumber}
                  onChange={(e) => setVisitNumber(e.target.value)}
                  placeholder="Örn: 1, 2, 3..."
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Bilgi</label>
                <input
                  type="text"
                  className={styles.input}
                  value={visitInfo}
                  onChange={(e) => setVisitInfo(e.target.value)}
                />
              </div>
              
              {/* Add the checkbox for repeat */}
              <div className={styles.checkboxGroup}>
                <label className={styles.label}>
                  <input
                    type="checkbox"
                    checked={willRepeat}
                    onChange={(e) => setWillRepeat(e.target.checked)}
                  />
                  Tekrar Edecek Mi?
                </label>
              </div>
              
              {/* Show repeat period section only if willRepeat is checked */}
              {willRepeat && (
                <>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Kapanış Visiti Tarihi</label>
                    <input
                      type="date"
                      className={styles.input}
                      required={willRepeat}
                      value={visitEndDate}
                      onChange={(e) => setVisitEndDate(e.target.value)}
                      min={visitDate}
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Tekrar Periyodu</label>
                    <div className={styles.periodInputGroup}>
                      <input
                        type="number"
                        min="1"
                        className={styles.input}
                        style={{flex: 2}}
                        required={willRepeat}
                        value={visitRepeatPeriod}
                        onChange={(e) => setVisitRepeatPeriod(e.target.value)}
                      />
                      <select
                        value={visitRepeatUnit}
                        onChange={(e) => setVisitRepeatUnit(e.target.value)}
                        className={styles.input}
                        style={{flex: 1}}
                        required={willRepeat}
                      >
                        <option value="day">Gün</option>
                        <option value="week">Hafta</option>
                        <option value="month">Ay</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
              
              {/* Add the checkbox for Google Calendar */}
              <div className={styles.checkboxGroup}>
                <label className={styles.label}>
                  <input
                    type="checkbox"
                    checked={addToCalendar}
                    onChange={(e) => setAddToCalendar(e.target.checked)}
                  />
                  Bu ziyareti Google Takvimime ekle
                </label>
              </div>
              
              <button 
                type="submit" 
                className={styles.submitButton}
              >
                Kaydet
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Job View/Delete Modal */}
      {showJobModal && selectedJob && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modal} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>İş Detayları</h3>
              <button 
                onClick={() => {
                  setShowJobModal(false);
                  setSelectedJob(null);
                }}
                className={styles.closeButton}
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.modalForm}>
              <div className={styles.inputGroup}>
                <label className={styles.label}>İş Başlığı</label>
                <div className={styles.readOnlyValue}>{selectedJob.title}</div>
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Başlangıç Tarihi</label>
                <div className={styles.readOnlyValue}>{selectedJob.startDate}</div>
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Bitiş Tarihi</label>
                <div className={styles.readOnlyValue}>{selectedJob.endDate}</div>
              </div>
              {selectedJob.time && (
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Saat</label>
                  <div className={styles.readOnlyValue}>{selectedJob.time}</div>
                </div>
              )}
              <div className={styles.inputGroup}>
                <label className={styles.label}>Durum</label>
                <div
                  className={`${styles.statusBadge} ${selectedJob.status ? styles.statusCompleted : styles.statusPending}`}
                >
                  {selectedJob.status ? '✓ Tamamlandı' : '○ Devam Ediyor'}
                </div>
              </div>
              <div className={styles.jobModalButtons}>
                <button 
                  onClick={handleToggleJobStatus}
                  className={selectedJob.status ? styles.markIncompleteButton : styles.markCompleteButton}
                >
                  {selectedJob.status ? 'Tamamlanmadı Olarak İşaretle' : '✓ Tamamlandı Olarak İşaretle'}
                </button>
                <button 
                  onClick={() => {
                    setShowJobModal(false);
                    setSelectedJob(null);
                  }}
                  className={styles.cancelButton}
                >
                  Kapat
                </button>
                <button 
                  onClick={handleDeleteJob}
                  disabled={isDeletingJob}
                  className={styles.deleteButton}
                >
                  <FaTrash style={{ marginRight: '6px' }} />
                  {isDeletingJob ? 'Siliniyor...' : 'İşi Sil'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Patient Modal */}
      {showAddPatientModal && selectedProject && (
        <AddPatientModal
          project={{ id: selectedProject }}
          onClose={() => setShowAddPatientModal(false)}
          onSave={handlePatientAdded}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {showTooltip && (
        <div
          className={styles.tooltip}
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
          }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};

export default PersonalCalendar;